import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RefreshCw, DollarSign, TrendingUp, Calendar as CalendarIcon, CalendarDays } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface DailyData {
  date: string;
  revenue: number;
  count: number;
}

interface PeriodData {
  revenue: number;
  count: number;
}

interface RevenueData {
  daily: DailyData[];
  today: PeriodData;
  week: PeriodData;
  month: PeriodData;
  total: PeriodData;
}

const AdminRevenueSection = () => {
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  const { data, isLoading, error, refetch } = useQuery<RevenueData>({
    queryKey: ["stripe-revenue"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("stripe-revenue");
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const formatEur = (v: number) => `${v.toFixed(2).replace(".", ",")} €`;

  const formatDateLabel = (dateStr: string) => {
    const d = new Date(dateStr + "T12:00:00");
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  };

  // Filter data by selected date range
  const filteredDaily = useMemo(() => {
    if (!data?.daily) return [];
    let filtered = data.daily;
    if (dateFrom) {
      const fromStr = dateFrom.toLocaleDateString("sv-SE");
      filtered = filtered.filter((d) => d.date >= fromStr);
    }
    if (dateTo) {
      const toStr = dateTo.toLocaleDateString("sv-SE");
      filtered = filtered.filter((d) => d.date <= toStr);
    }
    return filtered;
  }, [data?.daily, dateFrom, dateTo]);

  // Compute filtered totals
  const filteredTotals = useMemo(() => {
    let revenue = 0, count = 0;
    for (const d of filteredDaily) {
      revenue += d.revenue;
      count += d.count;
    }
    return { revenue, count };
  }, [filteredDaily]);

  const hasDateFilter = dateFrom || dateTo;

  const clearDates = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  // Quick presets
  const setPreset = (preset: string) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (preset) {
      case "today":
        setDateFrom(today);
        setDateTo(today);
        break;
      case "week": {
        const dow = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
        setDateFrom(monday);
        setDateTo(today);
        break;
      }
      case "month":
        setDateFrom(new Date(today.getFullYear(), today.getMonth(), 1));
        setDateTo(today);
        break;
      case "last-month": {
        const firstLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        setDateFrom(firstLastMonth);
        setDateTo(lastLastMonth);
        break;
      }
      case "all":
        clearDates();
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
        Chargement des données Stripe...
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <p className="text-destructive mb-4">Erreur lors du chargement des revenus</p>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" /> Réessayer
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-foreground">💰 Chiffre d'affaires Stripe</h2>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      </div>

      {/* Date Range Picker */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-muted-foreground font-medium">Période :</span>
            
            {/* From date */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={cn("min-w-[140px] justify-start text-left font-normal", !dateFrom && "text-muted-foreground")}>
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {dateFrom ? format(dateFrom, "dd MMM yyyy", { locale: fr }) : "Date début"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={setDateFrom}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                  disabled={(date) => date > new Date()}
                />
              </PopoverContent>
            </Popover>

            <span className="text-muted-foreground">→</span>

            {/* To date */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={cn("min-w-[140px] justify-start text-left font-normal", !dateTo && "text-muted-foreground")}>
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {dateTo ? format(dateTo, "dd MMM yyyy", { locale: fr }) : "Date fin"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={setDateTo}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                  disabled={(date) => date > new Date()}
                />
              </PopoverContent>
            </Popover>

            {hasDateFilter && (
              <Button variant="ghost" size="sm" onClick={clearDates} className="text-muted-foreground">
                ✕ Reset
              </Button>
            )}

            <div className="border-l border-border h-6 mx-1" />

            {/* Quick presets */}
            <div className="flex flex-wrap gap-1.5">
              <Button variant="secondary" size="sm" className="text-xs h-7" onClick={() => setPreset("today")}>Aujourd'hui</Button>
              <Button variant="secondary" size="sm" className="text-xs h-7" onClick={() => setPreset("week")}>Cette semaine</Button>
              <Button variant="secondary" size="sm" className="text-xs h-7" onClick={() => setPreset("month")}>Ce mois</Button>
              <Button variant="secondary" size="sm" className="text-xs h-7" onClick={() => setPreset("last-month")}>Mois dernier</Button>
              <Button variant="secondary" size="sm" className="text-xs h-7" onClick={() => setPreset("all")}>Tout</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Filtered period (or today if no filter) */}
        {hasDateFilter ? (
          <Card className="bg-card border-2 border-primary/30 md:col-span-2">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CalendarIcon className="w-4 h-4 text-primary" />
                <p className="text-muted-foreground text-sm">
                  {dateFrom && dateTo && dateFrom.toDateString() === dateTo.toDateString()
                    ? format(dateFrom, "dd MMM yyyy", { locale: fr })
                    : `${dateFrom ? format(dateFrom, "dd/MM", { locale: fr }) : "…"} → ${dateTo ? format(dateTo, "dd/MM", { locale: fr }) : "…"}`
                  }
                </p>
              </div>
              <p className="text-3xl font-bold text-primary">{formatEur(filteredTotals.revenue)}</p>
              <p className="text-xs text-muted-foreground">{filteredTotals.count} vente{filteredTotals.count > 1 ? "s" : ""}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="bg-card border-2 border-green-500/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  <p className="text-muted-foreground text-sm">Aujourd'hui</p>
                </div>
                <p className="text-2xl font-bold text-green-400">{formatEur(data.today.revenue)}</p>
                <p className="text-xs text-muted-foreground">{data.today.count} vente{data.today.count > 1 ? "s" : ""}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-2 border-blue-500/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarIcon className="w-4 h-4 text-blue-400" />
                  <p className="text-muted-foreground text-sm">Cette semaine</p>
                </div>
                <p className="text-2xl font-bold text-blue-400">{formatEur(data.week.revenue)}</p>
                <p className="text-xs text-muted-foreground">{data.week.count} vente{data.week.count > 1 ? "s" : ""}</p>
              </CardContent>
            </Card>
          </>
        )}

        <Card className="bg-card border-2 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="w-4 h-4 text-purple-400" />
              <p className="text-muted-foreground text-sm">Ce mois</p>
            </div>
            <p className="text-2xl font-bold text-purple-400">{formatEur(data.month.revenue)}</p>
            <p className="text-xs text-muted-foreground">{data.month.count} vente{data.month.count > 1 ? "s" : ""}</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-2 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-amber-400" />
              <p className="text-muted-foreground text-sm">Total (2 mois)</p>
            </div>
            <p className="text-2xl font-bold text-amber-400">{formatEur(data.total.revenue)}</p>
            <p className="text-xs text-muted-foreground">{data.total.count} vente{data.total.count > 1 ? "s" : ""}</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-base">
            Revenus journaliers {hasDateFilter ? "(période sélectionnée)" : "(30 derniers jours)"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={hasDateFilter ? filteredDaily : data.daily.slice(-30)}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tickFormatter={formatDateLabel} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${v}€`} />
                <Tooltip
                  contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))" }}
                  formatter={(value: number) => [formatEur(value), "Revenu"]}
                  labelFormatter={formatDateLabel}
                />
                <Area type="monotone" dataKey="revenue" stroke="hsl(142, 71%, 45%)" strokeWidth={2} fill="url(#revenueGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Sales Count Chart */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-base">Nombre de ventes par jour</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hasDateFilter ? filteredDaily : data.daily.slice(-30)}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tickFormatter={formatDateLabel} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))" }}
                  formatter={(value: number) => [value, "Ventes"]}
                  labelFormatter={formatDateLabel}
                />
                <Bar dataKey="count" fill="hsl(217, 91%, 60%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Daily breakdown table */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-foreground text-base">Détail journalier</CardTitle>
            {hasDateFilter && (
              <span className="text-sm text-muted-foreground">
                {filteredDaily.length} jour{filteredDaily.length > 1 ? "s" : ""} · Total : <span className="text-primary font-bold">{formatEur(filteredTotals.revenue)}</span>
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b border-border">
                  <th className="text-left p-2 text-muted-foreground">Date</th>
                  <th className="text-right p-2 text-muted-foreground">Ventes</th>
                  <th className="text-right p-2 text-muted-foreground">Revenu</th>
                </tr>
              </thead>
              <tbody>
                {[...filteredDaily].reverse().map((d) => (
                  <tr key={d.date} className="border-b border-border/50 hover:bg-muted/50">
                    <td className="p-2 text-foreground">
                      {new Date(d.date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })}
                    </td>
                    <td className="p-2 text-right text-foreground font-medium">{d.count}</td>
                    <td className="p-2 text-right text-green-400 font-bold">{formatEur(d.revenue)}</td>
                  </tr>
                ))}
                {filteredDaily.length === 0 && (
                  <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">Aucune donnée pour cette période</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRevenueSection;
